import type { AddressInfo, CursorMetaObj, MetaDataObj } from 'src/helper/shared-types';

export type WorkOrderEntity = {
  work_order_id: string;
  work_order_number: string;
  address: AddressInfo;
  problem_category?: string;
  timezone?: string;
  priority?: {
    value: string;
    label: string;
  };
  status?: {
    label: string;
    value: string;
    color_class: string;
  };
};

export type ServiceRequestEntity = {
  service_request_id: string;
  service_request_number: string;
  address: AddressInfo;
  problem_category?: string;
  timezone?: string;
  priority?: {
    value: string;
    label: string;
  };
  status?: {
    label: string;
    value: string;
    color_class: string;
  };
};

export type OperationalExceptionAction =
  | {
      kind: 'confirm_urgent_slot';
    }
  | {
      kind: 'open_scheduling';
      market_uuid?: string;
    }
  | {
      /** "Resident text needs a coordinator": the resident's text thread. */
      kind: 'open_resident_conversation';
      conversation_id: string;
      /** FF1: a text item on a job also names the job (uuids). */
      work_order_id?: string | null;
      service_request_id?: string | null;
    }
  | {
      /** BE-A: a hand-off on a job opens the job (uuids); its thread is the context's `conversation_id`. */
      kind: 'open_work_order';
      work_order_id: string;
      service_request_id?: string | null;
    };

/** BE-A: one of the resident's last inbound texts on a hand-off, oldest first. */
export type HandoffText = { text: string; received_at: string };

/**
 * A Work Queue item the backend describes itself (one with no work order or service
 * request behind it): what it says, whether it is still open, and where it opens.
 */
export type WorkQueueItem = {
  title?: string | null;
  summary?: string | null;
  status?: { value: string; label: string } | null;
  link?: { label: string; path: string } | null;
};

/**
 * 3.1 (G2-15): a text an alert offers, sent only when a coordinator clicks it. Late and no-show
 * items offer the vendor and resident texts; "resident stopped answering" offers the re-text.
 */
export type AlertTextStep = 'text_vendor' | 'text_resident' | 'retext_resident';

export type OperationalExceptionContext = {
  action?: OperationalExceptionAction;
  /** The one-click texts this item offers (G2-15). */
  text_steps?: AlertTextStep[];
  /** Resident texting hand-off: why a coordinator got it (e.g. `unanswerable_question`). */
  reason?: string;
  /** Resident texting hand-off: the question the resident is waiting on an answer to. */
  question?: string;
  /** 5.9: the "Move to On its own?" suggestion this item is about. */
  independence_suggestion_id?: string;
  /** The recommendation card this item shows (`RecommendationWorkQueue::context`). */
  recommendation_id?: string;
  /** 5.9: the capability it would move. */
  recommendation_kind?: string;
  /** Resident texting hand-off (BE-A): the resident's thread, also on a hand-off on a job. */
  conversation_id?: string;
  /** Resident texting hand-off (BE-A): the resident's last three inbound texts, oldest first. */
  recent_texts?: HandoffText[];
  /** Resident texting hand-off (BE-A): the card it came from, when one was approved or retired. */
  from_recommendation_id?: string;
};

import type { ItemWhy } from 'src/shared/types/itemWhy';
import type { TimeSensitivity } from 'src/shared/types/timeSensitivity';
import { WQ_TABS_KEYS } from 'src/views/WorkQueue/constant';
import type { QuoteType } from '../vendor-portal/work-order/types';

export type NotificationType = {
  cleared_at: string | null;
  created_at: string;
  data: {
    body?: string;
    condition_key?: string;
    context?: OperationalExceptionContext;
    group_key?: string;
    operational_exception_claim_id?: string;
    service_request_id?: string;
    title?: string;
    trip?: {
      trip_id: string;
      trip_number: number;
    };
    violation_codes?: string[];
    action_done_by?: string;
    note?: string;
    attributes?: {
      from: string;
      from_color_class: string;
      paused_reason: string;
      slug_of_to: string;
      to: string;
      to_color_class: string;
      is_rescheduled?: boolean;
      rescheduled_reason?: string;
      appointment_detail?: {
        schedule_end_date?: string;
        schedule_start_date?: string;
        technician?: string;
      };
    };
    schedule?: {
      new_date: string;
      new_start_time: string;
      new_end_time: string;
      old_start_time: string;
      old_end_time: string;
    };
    priority?: {
      value: string;
      label: string;
    };
    work_order_id?: string;
    /** A texting conversation's item: the conversation it is about. */
    conversation_id?: string;
    work_order_number?: string;
    quote_id?: string;
    quote_number?: string;
    quote?: QuoteType;
    due_date?: string;
    dispatched_at?: string;
    nte_amount_in_cents?: number;
    source_name?: string;
    pms_id?: string;
    user?: {
      name?: string;
      role?: string;
    };
    cancel_reason?: string;
    provider?: {
      name?: string;
      type?: string;
    };
    total_cost?: {
      min_in_cents?: number;
      max_in_cents?: number;
    };
    quote_total?: string;
    quote_tasks_count?: number;
    rejection_reason?: string;
    conversation?: {
      conversation_id?: string;
    };
    message_id?: string;
  };
  group_key?: string;
  grouped_count?: number;
  item?: WorkQueueItem;
  /** Demo (A-02/D39, FU-28): the server ranks this row first: its job is urgent and its work isn't done. */
  on_live_urgent_job?: boolean;
  /** 4.3: the setting or rule behind an alert item; only item rows carry one. */
  why?: ItemWhy | null;
  /** 30e: set on a scored row only. */
  time_sensitivity?: TimeSensitivity | null;
  notification_id: string;
  title?: string;
  body?: string;
  read_at: string | null;
  type: string;
  message?: {
    message_id?: string;
    content?: string;
    sender_type?: string;
    sender_name?: string;
    sender_role?: 'vendor' | 'technician' | string;
    created_at?: string;
  };
  user?: {
    name: string;
    user_id: string;
  };
  work_order?: {
    address: AddressInfo;
    problem_category?: string;
    timezone?: string;
    work_order_id: string;
    work_order_number: string;
    priority?: {
      value: string;
      label: string;
    };
    status?: {
      label: string;
      value: string;
      color_class: string;
    };
  };
  service_request?: {
    address: AddressInfo;
    problem_category?: string;
    timezone?: string;
    service_request_id: string;
    service_request_number: string;
    priority?: {
      value: string;
      label: string;
    };
    status?: {
      label: string;
      value: string;
      color_class: string;
    };
  };
  market?: {
    id: string;
    name: string;
  };
  unread_messages_count?: number;
  // Grouped notification fields for vendor.message.received notifications
  groupedCount?: number;
  groupedNotificationIds?: string[];
  latestMessageTimestamp?: string;
};

export type NotificationMetaObj = MetaDataObj & {
  next_cursor: string;
  prev_cursor: string;
};

/** The non-PII slice of the profile the coordinator layout resolves on the server. */
export type ServerProfile = Pick<ProfileInfo['data'], 'permissions' | 'markets'>;

export type ProfileInfo = {
  data: {
    name: string;
    email: string;
    user_id: string;
    permissions: string[];
    roles: string[];
    organization: {
      organization_id: string;
      name: string;
      logo_uri?: string;
      // Absent when unset — callers hide "Call Support" instead of substituting a number
      support_number?: string | null;
      has_lumi_feature_enabled: boolean;
      demo_reset_eligible?: boolean;
    };
    vendor?: {
      vendor_id: string;
      name: string;
      email: string;
      // ORI-587 onboarding gate signals (additive; BE ProfileResource vendor
      // block). `onboarding_complete` is the FE's primary gate signal;
      // `onboarding_status` mirrors vendors.on_boarding_status. Both are
      // optional so an older profile payload (missing the fields) is treated as
      // NOT complete — fail-closed — by the gate.
      onboarding_status?: 'pending' | 'complete';
      onboarding_complete?: boolean;
    };
    // Present only for a technician user (BE ProfileResource).
    technician_id?: string | null;
    markets?: Array<{ id: string; name: string }>;
    timezone: string;
    un_read_notification_count: number;
    profile_pic: string;
  };
  isLoading: boolean;
};

export type SubMenuObj = {
  name: string;
  view_uuid: string;
  /** The module's own "All" view (the server's primary view), whatever it is named. */
  is_primary?: boolean;
};

export type InitialProfileStateType = {
  entities: {
    roles: Record<string, unknown>;
  };
  app: {
    userDetails: ProfileInfo;
    theme: string;
    subMenus: { [key: string]: Array<SubMenuObj> };
    subMenusCountDetails: { [uuid: string]: number };
    fetchingSubMenusDetails: boolean;
    pusherConfig: {
      PUSHER_KEY: string;
      PUSHER_CLUSTER: string;
    };
  };
  notificationSource: {
    [key: string]: NotificationType;
  };
  notifications: {
    data: Array<string>;
    meta: NotificationMetaObj;
    /** The loaded list was requested oldest-first (e.g. `sort=created_at`). */
    ascending?: boolean;
  };
  clearedNotificationSource: {
    [key: string]: NotificationType;
  };
  clearedNotifications: {
    data: Array<string>;
    meta: NotificationMetaObj;
    /** The loaded list was requested oldest-first (e.g. `sort=created_at`). */
    ascending?: boolean;
  };
  signUp: {
    isLoading: boolean;
  };
  isThemeLoading: boolean;
  isAllNotificationFetching: boolean;
  isClearedNotificationFetching: boolean;
  isMarkAllReadLoading: boolean;
  activeSidebar: 'notifications' | 'bookmarks' | null;
  currentActiveTab: string;
  workOrderEntities: {
    [work_order_id: string]: WorkOrderEntity;
  };
  serviceRequestEntities: {
    [service_request_id: string]: ServiceRequestEntity;
  };
  ldIdentified: boolean;
  /**
   * APX-382 — `identify()` rejected, or never resolved inside
   * `LD_IDENTIFY_TIMEOUT_MS` of a flag-gated route mounting. Separate from
   * `ldIdentified` on purpose: flipping `ldIdentified` on failure would make every
   * existing consumer treat the anonymous (all-false) flag set as authoritative
   * and redirect orgs that do have access. This flag only ever ADDS a degraded
   * branch; `ldIdentified` keeps meaning "flags are trustworthy".
   */
  ldIdentifyFailed: boolean;
  markets: Array<{ label: string; value: string }>;
  /** Bumped by every live Work Queue item event over Pusher, so a screen can re-read even when the unread count stays put. */
  liveNotificationEvents: number;
  /** 10-29: the latest notification ids a live cleared event named (the last few hundred), for To do. */
  liveClearedNotificationIds?: string[];
  /** Bumped by every live Feedback card change (QQ4): Settings › Assistant › Feedback reads on it. */
  feedbackEvents?: number;
  /** The latest live Landon pause change (QQ3): the job it was on; `count` tells one from the next. */
  landonPauseEvent?: { workOrderId: string | null; count: number };
  /**
   * D15-29: what My Tasks › To do holds, for the sidebar's My Tasks badge: the server's items-only
   * count (D7, `notifications/count?todo=1`). Undefined until read in this session, or when To do
   * holds nothing.
   */
  todoCount?: string;
  /** Bumped when My Tasks decided or cleared an item, so the badge reads its count again (D7). */
  todoCountReads?: number;
  /** Bumped when To do's list should read again with no item event behind it (N1: a Take over or Hand back). */
  todoReads?: number;
  /** The `todoReads` ask whose read To do shows as it comes, new rows included (a Take over, N1). */
  todoReadShown?: number;
  /** True while To do is on screen: its own reads carry the badge's count (`meta.all_types_count`). */
  todoCountFromList?: boolean;
  /** Bumped when a job may have moved into or out of With Landon here (an Approve, a Hand back, P8). */
  withLandonReads?: number;
};

/**
 * APX-382 — how a degrade was concluded. `'timeout'` is an inference drawn from a
 * deadline elapsing and is never allowed to override a completed identification;
 * `'rejected'` comes from the SDK rejecting the call and is authoritative. See
 * `setLdIdentifyFailed` in `reducers.ts` for why the distinction is load-bearing.
 */
export type LdIdentifyFailureReason = 'timeout' | 'rejected';

export const initialProfileState: InitialProfileStateType = {
  entities: {
    roles: {},
  },
  app: {
    userDetails: {
      data: {},
      isLoading: true,
    } as ProfileInfo,
    theme: 'default',
    subMenus: {},
    subMenusCountDetails: {},
    fetchingSubMenusDetails: false,
    pusherConfig: {
      PUSHER_KEY: '',
      PUSHER_CLUSTER: '',
    },
  },
  notificationSource: {},
  notifications: {
    data: [],
    meta: {
      current_page: 0,
      from: 0,
      last: '',
      next: '',
      per_page: 0,
      prev: '',
      to: 0,
      total: 0,
      next_cursor: '',
      prev_cursor: '',
    },
  },
  clearedNotificationSource: {},
  clearedNotifications: {
    data: [],
    meta: {
      current_page: 0,
      from: 0,
      last: '',
      next: '',
      per_page: 0,
      prev: '',
      to: 0,
      total: 0,
      next_cursor: '',
      prev_cursor: '',
    },
  },
  signUp: {
    isLoading: false,
  },
  isThemeLoading: false,
  isAllNotificationFetching: false,
  isClearedNotificationFetching: false,
  isMarkAllReadLoading: false,
  activeSidebar: null,
  currentActiveTab: WQ_TABS_KEYS.ALL,
  workOrderEntities: {},
  serviceRequestEntities: {},
  ldIdentified: false,
  ldIdentifyFailed: false,
  markets: [],
  liveNotificationEvents: 0,
};

/**
 * APX-522 — the shape of the server-side first-page read of the Work Queue list.
 *
 * THIS IS THE OBJECT THAT CROSSES THE SERVER->CLIENT BOUNDARY (since APX-543). The
 * read runs in `src/server/work-queue-rows.ts`; `page.tsx` passes the result to
 * `<WorkQueue initialPage={...}>`, and because `WorkQueue` is a Client Component the
 * whole payload — including `source`, the RAW backend rows, unprojected — is
 * serialized into the RSC Flight payload and inlined in the document, in addition to
 * the rendered rows. Anything added to `NotificationType` therefore ships to the
 * browser on this route whether or not a cell renders it. That is the disclosure
 * surface the pilot's CloudFront release condition
 * (`src/server/ssr-pilot-flags.ts`) guards; widen it deliberately, not incidentally.
 *
 * Every field is reached by indexed access off {@link InitialProfileStateType}, so
 * it is DEFINITIONALLY the three slice fields it stands in for and cannot drift
 * from them.
 *
 * WHEN IT IS EVENTUALLY CONSUMED, IT MUST TRAVEL AS A PROP. The Redux store is a
 * module singleton (`src/redux/store.ts`) with no per-request factory, so any
 * SSR-time dispatch would leak one tenant's rows into another concurrent request.
 * `preloadedState` and slice hydration are disqualified on safety, not preference.
 */
export type WorkQueueInitialPage = {
  ids: InitialProfileStateType['notifications']['data'];
  source: InitialProfileStateType['notificationSource'];
  meta: NotificationMetaObj | CursorMetaObj;
};

/**
 * APX-522 — the outcome of the server-side Work Queue read, as a DISCRIMINATED
 * result rather than `WorkQueueInitialPage | null`.
 *
 * A bare `null` would conflate "this queue is genuinely empty" with "the server
 * read failed". That distinction matters because the client renders the
 * affirmative "You're all caught up" empty state for an empty row set, and telling
 * a user their queue is empty because a fetch timed out is a false statement — so
 * the failure case is named separately.
 *
 * LIVE SINCE APX-543 — this is shipped behaviour, not a forward contract (it read as
 * "NO CONSUMER TODAY ... logged and discarded" until that ticket landed).
 * `src/app/(coordinator)/work-queues/page.tsx` passes the result to
 * `<WorkQueue initialPage={...}>` and `src/views/WorkQueue/Listing.tsx` branches on the
 * discriminant:
 *   - `rows`        seeds the first paint from `page`, and holds the seeded rows with
 *                   their destructive actions disabled until the corrective client
 *                   fetch settles authoritatively;
 *   - `empty`       is not special-cased — today's empty state is already correct;
 *   - `unavailable` withholds the affirmative empty state until the client fetch
 *                   settles, and shows an explicit retry if that fetch also fails.
 */
export type WorkQueueSsrState =
  /** Rows were read and transformed. APX-543: seed the first paint from `page`. */
  | { status: 'rows'; page: WorkQueueInitialPage }
  /** The read succeeded and the queue really is empty. Today's empty state is correct. */
  | { status: 'empty' }
  /** No session / timeout / transport failure / unusable body. Assert NOTHING about the queue. */
  | { status: 'unavailable' };
